import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Phone, Thermometer, AlertTriangle, TrendingDown, Shield } from 'lucide-react';

const ThermalScanning = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Thermographic Inspections Ontario
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Expert infrared thermal imaging services to detect electrical issues before they become major problems. Ensure safety, efficiency, and compliance across your property.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <Link to="/contact">Book Consultation</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Thermal Scanning */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What Are Thermographic Inspections?
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                Thermographic inspections, also known as infrared (IR) inspections or thermal imaging, use specialized infrared cameras to capture temperature differences on the surface of objects and electrical systems.
              </p>
              <p className="text-lg text-gray-600 mb-4">
                These temperature variations are displayed as thermal images, revealing hidden problems such as electrical faults, insulation failures, overloaded circuits, and moisture intrusion that are invisible to the naked eye.
              </p>
              <p className="text-lg text-gray-600">
                By detecting abnormal heat signatures early, thermographic inspections help prevent costly repairs, equipment failures, and potential fire hazards.
              </p>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_next-day-electric/artifacts/f6izqs80_Capture.PNG"
                alt="Thermal scanning electrical inspection"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Thermographic Inspections Work
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our certified thermographers use advanced infrared cameras to scan your electrical systems and building structures
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <CardTitle>Infrared Scanning</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Our specialized infrared cameras capture thermal radiation emitted by objects, converting it into visible thermal images.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <CardTitle>Temperature Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Different colors represent different temperatures - warm areas appear in red/yellow, cool areas in blue/green.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <CardTitle>Expert Interpretation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Our trained thermographers analyze patterns and anomalies to identify potential issues and provide actionable recommendations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Applications of Thermal Scanning
            </h2>
            <p className="text-xl text-gray-600">
              Versatile solutions for residential, commercial, and industrial properties
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                  Electrical System Inspections
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Detect overloaded circuits and faulty breakers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Identify loose or corroded connections</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Find hot spots in electrical panels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Prevent electrical fires and failures</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="h-6 w-6 text-blue-600" />
                  Building & Facility Maintenance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Identify heat loss and poor insulation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Detect moisture intrusion and potential mold</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Locate roof and plumbing leaks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Assess HVAC system performance</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Thermometer className="h-6 w-6 text-orange-600" />
                  Industrial & Manufacturing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Monitor motors, bearings, and rotating equipment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Detect overheating components before failure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Inspect steam systems and heat exchangers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Reduce downtime and extend equipment life</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-6 w-6 text-green-600" />
                  Energy Audits & Efficiency
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Identify areas of significant heat loss</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Assess insulation effectiveness</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Test windows and door seals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Improve energy efficiency and reduce costs</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Thermal Scanning?
            </h2>
            <p className="text-xl text-gray-600">
              The advantages of thermographic inspections for your property
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Early Detection</h3>
              <p className="text-gray-600">
                Identify problems before they become major issues, saving time, money, and preventing potential hazards.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Non-Invasive & Safe</h3>
              <p className="text-gray-600">
                No physical contact required. Safe for operators and equipment with no need to shut down systems.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Comprehensive & Accurate</h3>
              <p className="text-gray-600">
                Captures data invisible to the naked eye, providing accurate pinpointing of issues for targeted repairs.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Compliance & Safety</h3>
              <p className="text-gray-600">
                Helps meet Ontario safety regulations and ensures systems are operating safely and efficiently.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                <TrendingDown className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Cost-Effective</h3>
              <p className="text-gray-600">
                Avoid costly emergency repairs and reduce downtime by addressing issues early through preventive maintenance.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                <Thermometer className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Detailed Reporting</h3>
              <p className="text-gray-600">
                Receive comprehensive reports with thermal images, expert analysis, and actionable recommendations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection Frequency */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              How Often Should You Conduct Thermal Inspections?
            </h2>
            <div className="space-y-4 text-lg text-gray-600">
              <p>
                The frequency of electrical thermal inspections depends on several factors including industry standards, facility type, age of electrical systems, and environmental conditions.
              </p>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Recommended Inspection Schedule:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Annual Inspections:</strong> Sufficient for most commercial and industrial facilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Bi-Annual/Quarterly:</strong> For high-risk industries or facilities with older systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>After Major Changes:</strong> Following system upgrades, repairs, or electrical incidents</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Schedule Your Thermal Inspection Today
          </h2>
          <p className="text-xl text-gray-800 mb-8">
            Protect your property and prevent costly repairs with professional thermographic inspections
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-blue-900 hover:bg-blue-800 text-white">
              <Link to="/contact">Book Consultation</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThermalScanning;